<template>
	<div>
		<div class="filters-wrap">
			<div class="filters-wrap__buttons">
				<BasicButton
					:disabled="isFiltersEmpty"
					:type="theme"
					@click="resetFilters"
					size="medium"
					tag="button"
				>
					view all
				</BasicButton>
				<MultipleSelect
					:list="multiSelectDepartments"
					:multirow="true"
					:value="activeDepartments"
					@input="onActiveDepartmentsChange"
					label="Select Discipline"
				/>
				<MultipleSelect
					:list="locations"
					:value="activeLocations"
					@input="onActiveLocationsChange"
					label="Select Location"
				/>
				<MultipleSelect
					:list="projects"
					:value="activeProjects"
					@input="onActiveProjectsChange"
					label="Select Project"
				></MultipleSelect>
			</div>
			<JobsInput
				:value="search"
				@input="onSearchInputChange"
				class="filters-wrap__input search"
			/>
			<span
				:class="`color-${theme}`"
				class="filters-wrap__positions text-bold"
				>{{ positionsLength }} Open Positions</span
			>
			<featured-positions-slider
				v-if="list"
				:list="list"
			></featured-positions-slider>
		</div>

		<JobsActiveFilters
			:list="activeFilters"
			@remove="removeFilter"
			class="filters-active"
			v-if="activeFilters.length"
		/>
	</div>
</template>

<script>
import MultipleSelect from "../common/MultipleSelect";
import JobsActiveFilters from "./JobsActiveFilters";
import BasicButton from "../BasicButton";
import JobsInput from "./JobsInput";
import { removeItem } from "../../utils/filters";
import debounce from "lodash.debounce";
import FeaturedPositionsSlider from "../sliders/FeaturedPositionsSlider";
import { getFeaturedPositions } from "../../services/featuredPositions";

const DEPARTMENTS_TYPE = "departments";
const LOCATION_TYPE = "locations";
const PROJECT_TYPE = "projects";

export default {
	name: "jobs-filter",
	components: {
		MultipleSelect,
		JobsActiveFilters,
		BasicButton,
		JobsInput,
		FeaturedPositionsSlider
	},
	props: {
		filters: Object,
		departments: Array,
		locations: Array,
		projects: Array,
		positionsLength: Number,
		theme: String
	},
	data() {
		return {
			activeDepartments: [],
			activeLocations: [],
			activeProjects: [],
			search: "",
			list: null
		};
	},
	computed: {
		multiSelectDepartments() {
			let items = [];
			if (this.departments && this.departments.length > 0) {
				let blockLimit = 6;
				let blockCounter = Math.ceil(
					this.departments.length / blockLimit
				);
				for (let j = 0; j < blockCounter; j++) {
					let block = [];
					for (let i = 0; i < blockLimit; i++) {
						let blockNumber = j * blockLimit + i;
						if (blockNumber < this.departments.length) {
							block.push(this.departments[blockNumber]);
						}
					}
					items.push(block);
				}
			}
			return items;
		},
		activeFilters() {
			return [
				...this.activeDepartments.map(item => ({
					type: DEPARTMENTS_TYPE,
					...item
				})),
				...this.activeLocations.map(item => ({
					type: LOCATION_TYPE,
					...item
				})),
				...this.activeProjects.map(item => ({
					type: PROJECT_TYPE,
					...item
				}))
			];
		},
		onFiltersChangeDebounced() {
			return debounce(this.onFiltersChange, 400);
		},
		isFiltersEmpty() {
			return (
				!this.search.length &&
				!this.activeLocations.length &&
				!this.activeDepartments.length &&
				!this.activeProjects.length
			);
		}
	},
	methods: {
		removeFilter({ id, type }) {
			switch (type) {
				case DEPARTMENTS_TYPE:
					this.removeDepartmentsItem(id);
					break;
				case LOCATION_TYPE:
					this.removeLocationsItem(id);
					break;
				case PROJECT_TYPE:
					this.removeProjectItem(id);
					break;
			}
		},
		removeDepartmentsItem(id) {
			this.activeDepartments = removeItem(this.activeDepartments, id);
			this.onFiltersChange();
		},
		removeLocationsItem(id) {
			this.activeLocations = removeItem(this.activeLocations, id);
			this.onFiltersChange();
		},
		removeProjectItem(id) {
			this.activeProjects = removeItem(this.activeProjects, id);
			this.onFiltersChange();
		},
		onActiveDepartmentsChange(list) {
			this.activeDepartments = list;
			this.onFiltersChange();
		},
		onActiveProjectsChange(list) {
			this.activeProjects = list;
			this.onFiltersChange();
		},
		onActiveLocationsChange(list) {
			this.activeLocations = list;
			this.onFiltersChange();
		},
		onSearchInputChange(e) {
			this.search = e.target.value;
			this.onFiltersChangeDebounced();
		},
		onFiltersChange() {
			const filters = {
				q: this.search,
				departments: this.activeDepartments.map(item => item.id),
				locations: this.activeLocations.map(item => item.id),
				projects: this.activeProjects.map(item => item.id)
			};

			this.$emit("change", filters);
		},
		setFilters(filters) {
			const { q, locations, departments, projects } = filters;
			if (q !== undefined) {
				this.search = q;
			}

			if (locations) {
				this.activeLocations = locations;
			}

			if (departments) {
				this.activeDepartments = departments;
			}

			if (projects) {
				this.activeProjects = projects;
			}
		},
		resetFilters() {
			if (this.isFiltersEmpty) {
				return;
			}

			const filters = {
				q: "",
				locations: [],
				departments: [],
				projects: []
			};
			this.setFilters(filters);
			this.$emit("change", filters);
		},
		async getFeatured() {
			try {
				const response = await getFeaturedPositions();
				this.list = response.filter(item => {
					if (item.position_id) {
						return true;
					}
				});
				console.log(this.list);
			} catch (e) {
				console.log("error loading featured positions");
			}
		}
	},
	created() {
		if (this.filters) {
			this.setFilters(this.filters);
		}
	},
	watch: {
		filters(val) {
			this.setFilters(val);
		}
	},
	async mounted() {
		await this.getFeatured();
	}
};
</script>

<style lang="scss" scoped type="text/scss">
@import "../../assets/scss/globals";

.filters-wrap__positions {
	font-family: Montserrat, sans-serif;
	font-weight: 600;
	font-size: 16px;
	letter-spacing: 0.32px;
}

.filters-wrap {
	padding-bottom: 30px;
	border-bottom: 1px solid #e7e6e6;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	@media (max-width: $mq-md-max) {
		.search {
			flex: 1;
			padding-right: 20px;
			max-width: 332px;
		}
	}
	@media (max-width: $mq-xs-max) {
		.search {
			max-width: 100%;
			padding-right: 0;
		}
		flex-direction: column;
		align-items: flex-start;
		padding-bottom: 30px;
	}

	& > * {
		margin-left: 10px;

		&:first-child {
			margin-left: 0;
		}

		@media (max-width: $mq-sm-max) {
			margin-top: 14px;
			margin-left: 0;

			&:first-child {
				margin-top: 0;
			}
		}
	}

	&__buttons {
		display: flex;

		@media (max-width: $mq-sm-max) {
			flex-basis: 100%;
		}

		@media (max-width: $mq-xs-max) {
			flex-direction: column;
			width: 100%;
		}

		& > * {
			margin-left: 10px;

			&:first-child {
				margin-left: 0;
			}

			@media (max-width: $mq-xs-max) {
				margin-left: 0;
				margin-top: 14px;

				&:first-child {
					margin-top: 0;
				}
			}
		}
	}

	&__input {
		width: 220px;

		@media (max-width: $mq-xs-max) {
			width: 100%;
		}
	}

	&__positions {
		margin-left: auto;
		font-size: 16px;
		letter-spacing: 0.32px;
	}
}

.filters-active {
	margin-top: 40px;
}
</style>



// WEBPACK FOOTER //
// src/components/jobs/JobsFilter.vue